import React, { useCallback, useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { RefreshCw, Megaphone, Send, BellRing, ChevronRight } from "lucide-react";

/**
 * A19-P5-UI — Admin Referral Campaigns dashboard.
 *
 * Path: /admin/referral-campaigns
 * Roles: admin / staff only (mirrored on backend with HTTP 403).
 *
 * Sections:
 *  • Table of all (non-deleted) referral_campaigns with KPI columns fetched
 *    from `GET /api/admin/referral-campaigns/{id}/stats` in parallel.
 *  • Per-campaign drawer with the full stats payload.
 *  • Two dry-run broadcast buttons per campaign (starting / ending_soon).
 *
 * No real emails are sent from this UI — dry_run=true is enforced.
 */
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
};

const AdminReferralCampaigns = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});      // {campaign_id: stats}
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api
        .get("/admin/referral-campaigns?limit=200")
        .then((r) => r.data?.campaigns || []);
      setRows(list);
      // Fetch all KPI cards in parallel.
      const results = await Promise.allSettled(
        list.map((c) => api.get(`/admin/referral-campaigns/${c.id}/stats`).then((r) => r.data))
      );
      const next = {};
      results.forEach((res, idx) => {
        if (res.status === "fulfilled") next[list[idx].id] = res.value;
      });
      setStats(next);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runDryRun = async (campaignId, broadcastType) => {
    setBusyId(`${campaignId}:${broadcastType}`);
    try {
      // Default to "today" for `starting`, and "ends_at date" for `ending_soon`.
      // Backend computes today automatically when `date` is omitted; for
      // `ending_soon` we explicitly target the campaign's ends_at date so
      // the operator can sanity-check the recipient list ahead of time.
      const params = new URLSearchParams();
      params.set("broadcast_type", broadcastType);
      params.set("dry_run", "true");
      if (broadcastType === "ending_soon") {
        const camp = rows.find((c) => c.id === campaignId);
        const d = (camp?.ends_at || "").slice(0, 10);
        if (d) params.set("date", d);
      }
      const r = await api.post(
        `/admin/referral-campaigns/boost-broadcast/run?${params.toString()}`
      );
      const rep = r.data;
      const c = rep.campaigns?.find((x) => x.campaign_id === campaignId) || {};
      toast.success(
        t("referrals.adminCampaigns.dryRunOk", {
          type: broadcastType,
          matched: rep.campaigns_matched ?? 0,
          recipients: c.recipients ?? 0,
          skipped: c.skipped_already_sent ?? 0,
        })
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "broadcast failed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-referral-campaigns-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-emerald-700" />
            {t("referrals.adminCampaigns.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {t("referrals.adminCampaigns.subtitle")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchAll}
          disabled={loading}
          data-testid="referral-campaigns-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t("referrals.adminCampaigns.refresh")}
        </Button>
      </header>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">
            {t("referrals.adminCampaigns.tableTitle", { n: rows.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table
            className="w-full text-sm border-collapse"
            data-testid="referral-campaigns-table"
          >
            <thead>
              <tr className="text-start text-xs uppercase text-slate-500 border-b border-slate-200">
                <th className="py-2 pe-3">{t("referrals.adminCampaigns.col.name")}</th>
                <th className="py-2 pe-3">{t("referrals.adminCampaigns.col.mult")}</th>
                <th className="py-2 pe-3">{t("referrals.adminCampaigns.col.profile")}</th>
                <th className="py-2 pe-3">{t("referrals.adminCampaigns.col.country")}</th>
                <th className="py-2 pe-3">{t("referrals.adminCampaigns.col.window")}</th>
                <th className="py-2 pe-3 text-end">{t("referrals.adminCampaigns.col.recipients")}</th>
                <th className="py-2 pe-3 text-end">{t("referrals.adminCampaigns.col.invSent")}</th>
                <th className="py-2 pe-3 text-end">{t("referrals.adminCampaigns.col.verified")}</th>
                <th className="py-2 pe-3 text-end">{t("referrals.adminCampaigns.col.extras")}</th>
                <th className="py-2 pe-3 text-end">{t("referrals.adminCampaigns.col.convRate")}</th>
                <th className="py-2 pe-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan="11" className="py-8 text-center text-slate-500" data-testid="referral-campaigns-empty">
                    {t("referrals.adminCampaigns.empty")}
                  </td>
                </tr>
              )}
              {rows.map((c) => {
                const s = stats[c.id] || {};
                return (
                  <tr
                    key={c.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`referral-campaign-row-${c.id}`}
                  >
                    <td className="py-2 pe-3">
                      <div className="font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">
                        {c.active
                          ? <Badge variant="default" className="bg-emerald-100 text-emerald-800">{t("referrals.adminCampaigns.active")}</Badge>
                          : <Badge variant="default" className="bg-slate-100 text-slate-600">{t("referrals.adminCampaigns.inactive")}</Badge>}
                      </div>
                    </td>
                    <td className="py-2 pe-3 font-mono">×{c.multiplier}</td>
                    <td className="py-2 pe-3">{c.target_profile || "—"}</td>
                    <td className="py-2 pe-3">{c.target_country || "—"}</td>
                    <td className="py-2 pe-3 text-xs text-slate-600 whitespace-nowrap">
                      {fmtDate(c.starts_at)} → {fmtDate(c.ends_at)}
                    </td>
                    <td className="py-2 pe-3 text-end font-mono">{s.recipients_reached ?? "—"}</td>
                    <td className="py-2 pe-3 text-end font-mono">{s.invitations_sent_during_boost ?? "—"}</td>
                    <td className="py-2 pe-3 text-end font-mono">{s.invitations_verified_during_boost ?? "—"}</td>
                    <td className="py-2 pe-3 text-end font-mono text-emerald-700">+{s.total_extra_points_distributed ?? 0}</td>
                    <td className="py-2 pe-3 text-end font-mono">
                      {s.conversion_rate != null ? `${(s.conversion_rate * 100).toFixed(1)}%` : "—"}
                    </td>
                    <td className="py-2 pe-3 text-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelected(c)}
                        data-testid={`referral-campaign-open-${c.id}`}
                        aria-label="open"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Drawer with the full stats payload + dry-run buttons. */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto"
          data-testid="referral-campaign-drawer"
        >
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle data-testid="referral-campaign-drawer-title">
                  {selected.name}
                </SheetTitle>
              </SheetHeader>

              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["multiplier", `×${selected.multiplier}`],
                    ["profile", selected.target_profile || "—"],
                    ["country", selected.target_country || "—"],
                    ["window", `${fmtDate(selected.starts_at)} → ${fmtDate(selected.ends_at)}`],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-slate-200 p-3">
                      <div className="text-[10px] uppercase text-slate-500">{t(`referrals.adminCampaigns.col.${k}`)}</div>
                      <div className="mt-1 font-medium text-slate-900 break-words">{v}</div>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                  <div className="text-xs uppercase text-emerald-800 font-semibold tracking-wide mb-3">
                    {t("referrals.adminCampaigns.kpiTitle")}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {(() => {
                      const s = stats[selected.id] || {};
                      const fields = [
                        ["recipients_reached", "recipients"],
                        ["starting_emails_sent", "startEmails"],
                        ["ending_emails_sent", "endEmails"],
                        ["invitations_sent_during_boost", "invSent"],
                        ["invitations_verified_during_boost", "verified"],
                        ["invitations_submitted_during_boost", "submitted"],
                        ["total_base_points", "basePoints"],
                        ["total_extra_points_distributed", "extras"],
                      ];
                      return fields.map(([k, label]) => (
                        <div key={k}>
                          <div className="text-[10px] uppercase text-slate-500">
                            {t(`referrals.adminCampaigns.kpi.${label}`)}
                          </div>
                          <div className="text-lg font-bold text-emerald-700 tabular-nums" data-testid={`kpi-${k}`}>
                            {s[k] ?? 0}
                          </div>
                        </div>
                      ));
                    })()}
                    <div className="col-span-2 border-t border-emerald-200 pt-2 mt-1">
                      <div className="text-[10px] uppercase text-slate-500">
                        {t("referrals.adminCampaigns.kpi.convRate")}
                      </div>
                      <div className="text-2xl font-bold text-emerald-700" data-testid="kpi-conversion_rate">
                        {stats[selected.id]?.conversion_rate != null
                          ? `${(stats[selected.id].conversion_rate * 100).toFixed(2)}%`
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide">
                    {t("referrals.adminCampaigns.actions")}
                  </div>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => runDryRun(selected.id, "starting")}
                    disabled={busyId === `${selected.id}:starting`}
                    data-testid="run-dry-starting"
                  >
                    <Send className="w-4 h-4" />
                    {t("referrals.adminCampaigns.dryRunStarting")}
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => runDryRun(selected.id, "ending_soon")}
                    disabled={busyId === `${selected.id}:ending_soon`}
                    data-testid="run-dry-ending"
                  >
                    <BellRing className="w-4 h-4" />
                    {t("referrals.adminCampaigns.dryRunEnding")}
                  </Button>
                  <p className="text-[11px] text-slate-500">
                    {t("referrals.adminCampaigns.dryRunNote")}
                  </p>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminReferralCampaigns;
